import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { TravelBufferMockup } from '../components/visuals/TravelBufferMockup.js';
import { IconTrades } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-mobile-dog-grooming')({
  component: MobileDogGroomingPage,
});

function MobileDogGroomingPage() {
  usePageTitle('Bookings for mobile dog groomers');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For mobile dog groomers',
  icon: IconTrades,
  headline: 'Tight routes. Tighter timing.',
  headlineEmphasis: 'Tighter timing',
  subhead:
    'Bookaburra books the right service length, fits the next visit close to the last one, and never lets you stack a husky and a poodle back-to-back.',
  Visual: TravelBufferMockup,
  business: {
    name: 'Pawfect Pups Mobile',
    location: 'Adelaide · 1 mobile setup · 6–8 dogs a day',
    summary:
      'Picture a one-person mobile dog grooming business covering Adelaide’s inner suburbs. The rig is a small grooming trailer towed behind a normal car — what most groomers actually use rather than a dedicated van. Service times vary by dog: short-coat tidy-ups take 45 minutes, full grooms 90 minutes, and a big-breed deshed runs to 2 hours. The trailer is the workshop, so every job is on the client’s driveway.',
  },
  dayTimeline: [
    {
      time: '7:30',
      event:
        'Day starts in Norwood. Five dogs booked — two full grooms, two short-coat tidies, one big-breed deshed in the afternoon.',
      bookaburraResponse:
        'Nothing to do. Each booking blocked the right service duration and clustered to the same suburb run.',
    },
    {
      time: '9:00',
      event:
        'A new client books a 90-minute full groom for her labradoodle and picks tomorrow 13:30 from the picker.',
      bookaburraResponse:
        'Only slots with a full 90 minutes free — plus a travel buffer either side — are offered. 13:30 fits cleanly between the Norwood morning run and the afternoon job.',
    },
    {
      time: '10:45',
      event:
        'A client in Henley Beach opens the booking page looking for a tidy late morning.',
      bookaburraResponse:
        '11:30 isn’t offered — the system already knows the 30-minute hop from Norwood would breach the travel buffer. Earliest west-side window: Thursday morning when the trailer’s out that way anyway.',
    },
    {
      time: '12:30',
      event:
        'On Sue’s driveway in Norwood. Her neighbour spots the trailer and asks if her chihuahua can be done while you’re there — a quick 30-minute tidy.',
      bookaburraResponse:
        'She books in via the booking page on her phone. Earliest offered slot on the same driveway is 12:55 — the system reserves a 10-minute same-postcode buffer for cleaning down and setup.',
    },
    {
      time: '14:00',
      event:
        'Follow-up list shows 6 regulars overdue past their 6-week cadence.',
      bookaburraResponse:
        'One batch of magic-link "time for a groom?" SMS goes out. Three self-book into next week’s Norwood and Burnside rounds.',
    },
    {
      time: '15:30',
      event:
        'A client realises she’s on holiday next week when her booking falls — she clicks reschedule in her morning reminder.',
      bookaburraResponse:
        'Magic-link reschedule re-runs the route + duration checks. She picks the week after — the freed slot opens up for the allocator to absorb.',
    },
  ],
  pains: [
    {
      quote:
        'A golden retriever full groom got booked back-to-back with a quick puppy tidy across town. No time to drive, let alone refill the water tank.',
      explanation:
        'The booking page treated a 90-minute groom and a 45-minute groom as the same slot.',
    },
    {
      quote:
        'I drove Norwood → Henley Beach → Norwood in one morning because the slots came out in the wrong order.',
      explanation:
        'Each booking looked fine on its own. Together they were an hour of unpaid driving.',
    },
    {
      quote:
        'A regular client’s spoodle is two weeks past due and I only noticed when I saw the dog at the dog park.',
      explanation:
        'There was no list of clients I should be ringing. They quietly drifted off the schedule.',
    },
  ],
  solutions: [
    {
      title: 'Per-service duration',
      body: (
        <>
          A 90-minute groom blocks 90 minutes. A 45-minute tidy blocks 45.
          Clients only see slots their dog’s service actually fits into.
        </>
      ),
    },
    {
      title: 'Travel-aware allocator',
      body: (
        <>
          The next available slot has to fit the day’s geography. If you’re
          finishing in Norwood at 11am, Henley Beach at 11:30am is never offered.
        </>
      ),
    },
    {
      title: 'Follow-up list',
      body: (
        <>
          Bookaburra surfaces regulars whose last visit is overdue, so the
          six-weekly clients you rely on never quietly drift off the books.
        </>
      ),
    },
    {
      title: 'Magic-link reschedule',
      body: (
        <>
          Owner heading away on holiday moves the slot herself from the link in
          the reminder text. No phone tag, no double-booked driveway.
        </>
      ),
    },
  ],
  industrySlug: 'mobile-dog-grooming',
};
